import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Button from "../../../../common/Button";
import {
  updateInterventionAssessment,
  updateInterventionStatus,
} from "../../../../../redux/modules/intervention/actions";
import { findAvailableStep } from "../../../text/data";
import CustomInput from "../../../../common/CustomInput";
import NotificationCreate from "../../../clinical-services/service-steps/education/NotificationCreate";
import NotificationCard from "../../../clinical-services/service-steps/education/notification-info-card";
import { InterventionCompletion } from "../components/index";
import { InterventionStatus } from "../data";

const generateUuid = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID)
    return crypto.randomUUID();
  const s = [];
  const hex = "0123456789abcdef";
  for (let i = 0; i < 36; i++)
    s[i] = hex.substr(Math.floor(Math.random() * 16), 1);
  s[14] = "4";
  s[19] = hex.substr((parseInt(s[19], 16) & 0x3) | 0x8, 1);
  s[8] = s[13] = s[18] = s[23] = "-";
  return s.join("");
};

const Notification = ({ intervention, currentInterventionSteps, currentStep: activeStep = 5 }) => {
  const dispatch = useDispatch();
  const { id: patientId, interventionId } = useParams();
  const { patient } = useSelector((state) => state.patients);

  // Local state bound to nested notification object
  const [notificationType, setNotificationType] = useState("");
  const [notificationDate, setNotificationDate] = useState("");
  const [notificationMethod, setNotificationMethod] = useState("");
  const [notificationInformations, setNotificationInformations] = useState([]);
  const [pharmacyLocation, setPharmacyLocation] = useState("");
  const [billingInformation, setBillingInformation] = useState("");
  const [openServiceCompleteDialog, setOpenServiceCompleteDialog] =
    useState(false);
  const [serviceEndTime, setServiceEndTime] = useState(
    new Date().toISOString()
  );

  // Slider/create/edit state
  const [showNotificationSlider, setShowNotificationSlider] = useState(false);
  const [createNotification, setCreateNotification] = useState(true);
  const [editingNotification, setEditingNotification] = useState(null);
  const getTodayDateValue = () => new Date().toISOString().split("T")[0];
  const selectedPrimaryProvider =
    patient?.careProvider?.find((provider) => provider?.primaryProvider) ||
    patient?.careProvider?.[0] ||
    null;

  // Options (copied/adapted from PatientNotification.jsx)
  const notificationTypeOptions = [
    { value: "same", label: "Same as Referral Type" },
    { value: "nonApplicable", label: "Non-Applicable" },
    { value: "Primary Provider", label: "Primary Provider" },
    { value: "Nurse Practitioner", label: "Nurse Practitioner" },
    {
      value: "Walk In Clinic - No Primary Provider",
      label: "Walk In Clinic - No Primary Provider",
    },
    {
      value: "Emergency - No Primary Provider",
      label: "Emergency - No Primary Provider",
    },
    { value: "Other Healthcare Provider", label: "Other Healthcare Provider" },
  ];

  const notificationMethodOptions = [
    { value: "phone", label: "Phone" },
    { value: "fax", label: "FAX" },
    { value: "email", label: "Email" },
    { value: "inPerson", label: "In Person" },
    { value: "mail", label: "Mail" },
    { value: "secureMessage", label: "Secure Message" },
  ];

  // Initialize from intervention state (do not override when user has started editing)
  useEffect(() => {
    const formNotification =
      intervention?.interventionAssessment?.interventionAssessmentForm
        ?.notification || {};

    const notifType = Array.isArray(formNotification.notificationType)
      ? formNotification.notificationType[0] || ""
      : formNotification.notificationType || "";

    const shouldHydrateFromStore =
      notificationType === "" &&
      notificationDate === "" &&
      notificationMethod === "" &&
      (notificationInformations?.length || 0) === 0;

    if (shouldHydrateFromStore) {
      setNotificationType(notifType || "");
      setNotificationDate(formNotification.dateOfNotification || "");
      setNotificationMethod(formNotification.methodOfNotification || "");
      setNotificationInformations(
        formNotification.notificationInformations || []
      );
      setPharmacyLocation(formNotification.pharmacyLocation || "");
      setBillingInformation(formNotification.billingInformation || "");
    }
  }, [intervention]);

  // Auto-fill today's date when date is empty (except Non-Applicable).
  useEffect(() => {
    if (notificationType === "nonApplicable") return;
    if (!notificationDate) {
      setNotificationDate(getTodayDateValue());
    }
  }, [notificationType, notificationDate]);

  useEffect(() => {
    const hasProvider = Boolean(selectedPrimaryProvider);
    if (!hasProvider) return;
    if (notificationType) return;
    if ((notificationInformations || []).length > 0) return;

    handleNotificationTypeChange({ value: "Primary Provider" });
  }, [selectedPrimaryProvider, notificationType, notificationInformations]);

  const persistNotification = async (partial = {}) => {
    const payload = {
      notification: {
        notificationType: notificationType ? [notificationType] : [],
        dateOfNotification: notificationDate || null,
        methodOfNotification: notificationMethod || null,
        notificationInformations: notificationInformations || [],
        pharmacyLocation: pharmacyLocation || null,
        billingInformation: billingInformation || null,
        ...partial,
      },
    };
    await dispatch(updateInterventionAssessment(interventionId, payload));
  };

  // Handlers
  const handleNotificationTypeChange = (opt) => {
    const value = opt?.value || "";
    setNotificationType(value);
    // Match referrals.jsx behaviors
    if (value === "nonApplicable") {
      setNotificationDate("");
      setNotificationMethod("");
      setNotificationInformations([]);
      return;
    }

    if (value === "Primary Provider") {
      const existingProvider = selectedPrimaryProvider;
      if (existingProvider) {
        const primaryProvider = {
          referralId: null,
          referralType: "Primary Provider",
          clinicName: existingProvider?.clinicName || "",
          firstName: existingProvider?.firstName || "",
          lastName: existingProvider?.lastName || "",
          middleName: existingProvider?.middleName || "",
          licenseNumber: existingProvider?.licenseNumber || "",
          designation:
            existingProvider?.title || existingProvider?.designation || "",
          isPrimaryPhysician: !!existingProvider?.primaryProvider,
          phoneNumber:
            existingProvider?.contactInfo?.primaryPhone ||
            existingProvider?.phoneNumber ||
            "",
          fax:
            existingProvider?.contactInfo?.fax || existingProvider?.fax || "",
          email:
            existingProvider?.contactInfo?.email ||
            existingProvider?.emailAddress ||
            existingProvider?.email ||
            "",
          emailAddress:
            existingProvider?.contactInfo?.email ||
            existingProvider?.emailAddress ||
            existingProvider?.email ||
            "",
          address:
            existingProvider?.contactInfo?.address ||
            existingProvider?.address ||
            "",
          unitNumber:
            existingProvider?.contactInfo?.unitNumber ||
            existingProvider?.unitNumber ||
            "",
          poBox:
            existingProvider?.contactInfo?.poBox ||
            existingProvider?.poBox ||
            "",
          city:
            existingProvider?.contactInfo?.city || existingProvider?.city || "",
          province:
            existingProvider?.contactInfo?.province ||
            existingProvider?.province ||
            "",
          postalCode:
            existingProvider?.contactInfo?.postalCode ||
            existingProvider?.postalCode ||
            "",
          country:
            existingProvider?.contactInfo?.country ||
            existingProvider?.country ||
            "",
          comments:
            existingProvider?.contactInfo?.comments ||
            existingProvider?.comments ||
            "",
          notificationType: "Primary Provider",
          dateOfNotification: notificationDate || getTodayDateValue(),
          methodOfNotification: notificationMethod || "fax",
        };
        const updated = [...(notificationInformations || []), primaryProvider];
        setNotificationInformations(updated);
        // Persist because notificationInformations changed
        const finalDate = primaryProvider.dateOfNotification;
        const finalMethod = primaryProvider.methodOfNotification;
        if (!notificationDate) setNotificationDate(finalDate);
        if (!notificationMethod) setNotificationMethod(finalMethod);
        persistNotification({
          notificationType: ["Primary Provider"],
          dateOfNotification: finalDate,
          methodOfNotification: finalMethod,
          notificationInformations: updated,
        });
        return;
      }
      // No existing provider -> open slider
      if (!notificationDate) setNotificationDate(getTodayDateValue());
      if (!notificationMethod) setNotificationMethod("fax");
      openCreateNotification();
      return;
    }

    if (value && value !== "same") {
      if (!notificationDate) setNotificationDate(getTodayDateValue());
      if (!notificationMethod) setNotificationMethod("fax");
      openCreateNotification();
    }
  };

  const handleMethodChange = (opt) => {
    setNotificationMethod(opt?.value || "");
  };

  const openCreateNotification = () => {
    setEditingNotification(null);
    setCreateNotification(true);
    setShowNotificationSlider(true);
  };

  const onEditNotification = (notification) => {
    setEditingNotification(notification);
    setCreateNotification(false);
    setShowNotificationSlider(true);
  };

  const onDeleteNotification = async (notification) => {
    const remaining = (notificationInformations || []).filter(
      (n) => n !== notification
    );
    setNotificationInformations(remaining);
    if (remaining.length === 0) {
      // When last card is deleted, switch to Non-Applicable and clear date/method
      setNotificationType("nonApplicable");
      setNotificationDate("");
      setNotificationMethod("");
      await persistNotification({
        notificationType: ["nonApplicable"],
        dateOfNotification: null,
        methodOfNotification: null,
        notificationInformations: [],
      });
    } else {
      // Persist because notificationInformations changed
      await persistNotification({ notificationInformations: remaining });
    }
  };

  const handleNotificationSave = async (info) => {
    const newInfo = {
      referralId: info.referralId ?? null,
      referralType: info.referralType || notificationType,
      clinicName: info.clinicName || "",
      firstName: info.firstName || "",
      lastName: info.lastName || "",
      middleName: info.middleName || "",
      licenseNumber: info.licenseNumber || "",
      designation: info.designation || "",
      isPrimaryPhysician: info.isPrimaryPhysician ?? true,
      phoneNumber: info.phoneNumber || "",
      fax: info.fax || "",
      email: info.email || "",
      emailAddress: info.email || info.emailAddress || "",
      address: info.address || "",
      unitNumber: info.unitNumber || "",
      poBox: info.poBox || "",
      city: info.city || "",
      province: info.province || "",
      postalCode: info.postalCode || "",
      country: info.country || "",
      comments: info.comments || "",
      notificationType: info.referralType || notificationType,
      dateOfNotification: notificationDate || getTodayDateValue(),
      methodOfNotification: notificationMethod || "fax",
    };

    const updated = [...(notificationInformations || []), newInfo];
    setNotificationInformations(updated);
    await persistNotification({
      notificationType: [notificationType],
      dateOfNotification: newInfo.dateOfNotification,
      methodOfNotification: newInfo.methodOfNotification,
      notificationInformations: updated,
    });
    setShowNotificationSlider(false);
    setCreateNotification(true);
  };

  const handleNotificationUpdate = async (info) => {
    const updatedInfo = {
      ...editingNotification,
      ...info,
      referralId: info.referralId ?? editingNotification?.referralId ?? null,
      referralType:
        info.referralType ||
        editingNotification?.referralType ||
        notificationType,
      notificationType:
        info.referralType ||
        editingNotification?.referralType ||
        notificationType,
      dateOfNotification:
        notificationDate ||
        editingNotification?.dateOfNotification ||
        getTodayDateValue(),
      methodOfNotification:
        notificationMethod ||
        editingNotification?.methodOfNotification ||
        "fax",
    };

    const updated = (notificationInformations || []).map((n) =>
      n?.referralId === updatedInfo.referralId ? updatedInfo : n
    );
    setNotificationInformations(updated);
    await persistNotification({
      notificationType: [notificationType],
      dateOfNotification: updatedInfo.dateOfNotification,
      methodOfNotification: updatedInfo.methodOfNotification,
      notificationInformations: updated,
    });
    setShowNotificationSlider(false);
    setCreateNotification(true);
    setEditingNotification(null);
  };

  // Navigation functions
  const onNext = async () => {
    try {
      // Replace with Save & Complete flow: open modal
      setServiceEndTime(new Date().toISOString());
      setOpenServiceCompleteDialog(true);
    } catch (error) {
      console.error("Failed to update intervention step:", error);
    }
  };

  const onPrevious = async () => {
    try {
      // Find previous available step using common function
      const currentStep =
        activeStep ||
        intervention?.interventionAssessment?.interventionAssessmentForm
          ?.currentStep ||
        5;
      const previousStep = findAvailableStep(
        currentStep,
        currentInterventionSteps,
        -1
      );

      await dispatch(
        updateInterventionAssessment(interventionId, {
          currentStep: previousStep,
        })
      );
    } catch (error) {
      console.error("Failed to update intervention step:", error);
    }
  };

  // Determine if buttons should be enabled
  const documentationIndex = Array.isArray(currentInterventionSteps)
    ? currentInterventionSteps.findIndex(
        (s) => s.name === "Documentation" && !s.disabled && !s.comingSoon
      )
    : -1;
  const status = intervention?.status;
  const isWaiting = status === InterventionStatus.WaitingProviderResponse;
  const canGoNext = !!notificationType; // require a notification type to proceed
  const canGoPrevious = true; // Can go back to step 4
  if (!intervention) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="text-gray-400 text-xl font-medium mb-4">
            No Intervention Data
          </div>
          <div className="text-gray-500 text-base">
            Notification information is not available.
          </div>
        </div>
      </div>
    );
  }

  // Derived display object from local state
  const notification = {
    notificationType: notificationType,
    dateOfNotification: notificationDate,
    methodOfNotification: notificationMethod,
  };

  return (
    <>
      <div className="w-full max-h-fit bg-white overflow-auto">
        <div className="w-full h-full flex flex-col -ml-5">
          <div className="flex gap-8 px-6 py-3 flex-1">
            <div className="flex flex-row gap-6 overflow-auto min-w-0 h-full">
              {/* Notification of Service card */}
              <div
                id="notificationOfService"
                className={`h-[610px] w-[550px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col `}
              >
                {/* <div
                  className="items-center justify-start flex flex-row px-5 py-4 bg-cover bg-center"
                  style={{
                    backgroundImage: `url(/icons/texture-background.png)`,
                  }}
                >
                  <h3 className="text-teal-300 font-semibold text-lg ">
                    Notification of Intervention
                  </h3>
                </div> */}
                <div className="h-[72px] gap-[10px] opacity-1 px-6 py-3 bg-gray-20 border border-gray-100 flex items-center justify-between">
                  <h3 className="text-teal-300 font-semibold text-xl">
                    Notification of Intervention
                  </h3>
                </div>
                <div className="flex flex-col gap-5 w-full flex-1 min-h-0 px-5 py-5 overflow-y-auto overflow-x-hidden bg-gray-10">
                  <div className="flex flex-col gap-5 border-b border-gray-100 pb-5">
                    <div className="flex flex-col gap-4">
                      <p className="text-lg font-semibold text-gray-800">
                        Notification Type
                      </p>
                    </div>
                    <div>
                      <CustomInput
                        options={notificationTypeOptions.filter(
                          (o) => o.value !== "same"
                        )}
                        name="notificationType"
                        type="dropdown"
                        className="!bg-white"
                        value={notificationType}
                        onChange={(e) => handleNotificationTypeChange(e)}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-4">
                    <p className="text-lg font-semibold text-gray-800">
                      Date of Notification
                    </p>
                  </div>
                  <div>
                    <CustomInput
                      name="dateOfNotification"
                      type="date"
                      className="!bg-white"
                      disabled={notificationType === "nonApplicable"}
                      value={notificationDate}
                      onChange={(value) => setNotificationDate(value)}
                    />
                  </div>
                  <div className="flex flex-col gap-4">
                    <p className="text-lg font-semibold text-gray-800">
                      Method of Notification
                    </p>
                  </div>
                  <div>
                    <CustomInput
                      options={notificationMethodOptions}
                      name="methodOfNotification"
                      type="dropdown"
                      className="!bg-white"
                      disabled={notificationType === "nonApplicable"}
                      value={notificationMethod}
                      onChange={(e) => handleMethodChange(e)}
                      shortMenuHeight={true}
                    />
                  </div>
                </div>
              </div>

              {/* notification informations cards */}
              {notificationInformations &&
                notificationInformations.length > 0 && (
                  <div className="flex flex-col gap-6 ml-2 w-full min-w-0 h-full xl:flex-none xl:w-[520px]">
                    {notificationInformations.map((n, idx) => (
                      <NotificationCard
                        key={n?.referralId || idx}
                        notification={n}
                        layoutVariant="intervention"
                        onEdit={() => onEditNotification(n)}
                        onDelete={() => onDeleteNotification(n)}
                      />
                    ))}
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between space-x-4">
        <div className="w-full">{/* Progress indicator could go here */}</div>
        <div className="flex items-center gap-3">
          <Button
            type="primary-outline"
            text="Back"
            className="w-[200px] max-h-[52px] !rounded-[30px]"
            onClick={onPrevious}
            disabled={!canGoPrevious}
          />
          {isWaiting ? (
            <Button
              type="primary"
              text="Next"
              className="w-[200px] max-h-[52px] !rounded-[30px]"
              onClick={async () => {
                try {
                  const targetStep = documentationIndex + 1;
                  await dispatch(
                    updateInterventionAssessment(interventionId, {
                      currentStep: targetStep,
                    })
                  );
                } catch (e) {
                  console.error("Failed to navigate to Documentation:", e);
                }
              }}
              disabled={!canGoNext}
            />
          ) : (
            <Button
              type="primary"
              text="Save & Complete"
              className="w-[200px] max-h-[52px] !rounded-[30px]"
              onClick={onNext}
              disabled={!canGoNext}
            />
          )}
        </div>
      </div>

      {/* Notification Create / Update Slider */}
      <NotificationCreate
        create={createNotification}
        type={notificationType}
        updateType={(val) => setNotificationType(val)}
        open={showNotificationSlider}
        existingNotification={editingNotification}
        onClose={() => {
          setCreateNotification(true);
          setShowNotificationSlider(false);
          setEditingNotification(null);
        }}
        onSave={handleNotificationSave}
        onUpdate={handleNotificationUpdate}
      />

      {/* Completion Modal */}
      <InterventionCompletion
        open={openServiceCompleteDialog}
        onClose={() => setOpenServiceCompleteDialog(false)}
        onConfirm={async () => {
          try {
            // Save billing info into notification and set status to waiting
            await persistNotification({
              billingInformation: billingInformation || null,
            });
            await dispatch(
              updateInterventionStatus(
                patientId,
                interventionId,
                InterventionStatus.WaitingProviderResponse
              )
            );

            // Move to Documentation tab
            const currentStep =
              activeStep ||
              intervention?.interventionAssessment?.interventionAssessmentForm
                ?.currentStep ||
              5;
            const docIndex = Array.isArray(currentInterventionSteps)
              ? currentInterventionSteps.findIndex(
                  (s) => s.name === "Documentation"
                )
              : -1;
            const documentationStep =
              docIndex >= 0
                ? docIndex + 1
                : findAvailableStep(currentStep, currentInterventionSteps, 1);
            await dispatch(
              updateInterventionAssessment(interventionId, {
                currentStep: documentationStep,
                endDateTime: new Date().toISOString(),
              })
            );

            setOpenServiceCompleteDialog(false);
          } catch (e) {
            console.error("Failed to complete intervention:", e);
          }
        }}
        intervention={intervention}
        billingInformation={billingInformation}
        onBillingInformationChange={setBillingInformation}
        disableConfirm={billingInformation === ""}
      />
    </>
  );
};

export default Notification;
